import React from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import { parseDocCitationUrl } from '../../../lib/audit/citationIntegrityVerifier';
import { analysisCardSummary } from '../findings/analysisCardSummary';
import { stripConfidenceComments } from '../../../lib/reportRendering/narrativeArtifacts';

/** Shared by prose and concise saved values; uncertainty remains visible. */
export function savedScreeningDisplayText(text: string, showReferences = true): string {
  const annotated = stripConfidenceComments(text.replace(
    /<!-{2,}\s*confidence:\s*([\s\S]*?)(?:-{2,}!?>|⟶|—>)\s*(?:⟶|—>)?/gi,
    (_comment, confidence: string) => `\n\nScreening confidence: ${confidence.trim()}.\n\n`,
  ));
  return showReferences ? annotated : analysisCardSummary(annotated);
}

/** Display-only: keep every condition and citation, without leaking transport
 * syntax into the reading column or changing the immutable saved report. */
export function SavedScreeningProse({ text, className = 'text-sm leading-7 text-text-secondary', style, renderPlainText, showReferences = true }: {
  text: string;
  className?: string;
  style?: React.CSSProperties;
  showReferences?: boolean;
  renderPlainText?: (value: string) => React.ReactNode;
}) {
  const prose = savedScreeningDisplayText(text, showReferences);
  function sourceLink(url: string, label?: React.ReactNode): React.ReactNode {
    let source: ReturnType<typeof parseDocCitationUrl> = null;
    try { source = parseDocCitationUrl(url); } catch { /* Preserve malformed saved citations as text. */ }
    if (!source || source.docId.startsWith('sim/')) return label ? <>{label} ({url})</> : url;
    const params = new URLSearchParams({ page: String(source.page) });
    if (source.charEnd > source.charStart) params.set('highlight', `${source.charStart},${source.charEnd}`);
    return <a href={`/docs/${encodeURIComponent(source.docId)}?${params}`} title={url}
      aria-label={`Open source ${source.docId}, page ${source.page}`}>
      {label ?? `Source, p. ${source.page}`}
    </a>;
  }
  function renderText(value: string): React.ReactNode {
    return value.split(/(doc:\/\/[^\s)\],;"']+)/gi).map((part, index) => {
      if (!/^doc:\/\//i.test(part)) return renderPlainText ? <React.Fragment key={index}>{renderPlainText(part)}</React.Fragment> : part;
      const url = part.replace(/[.!?]+$/, '');
      return <React.Fragment key={index}>{sourceLink(url)}{part.slice(url.length)}</React.Fragment>;
    });
  }
  function renderChildren(children: React.ReactNode): React.ReactNode {
    return React.Children.map(children, child => {
      if (typeof child === 'string') return renderText(child);
      if (React.isValidElement<{ children?: React.ReactNode }>(child) && child.type !== 'a' && child.type !== 'code') {
        return React.cloneElement(child, {}, renderChildren(child.props.children));
      }
      return child;
    });
  }
  return <div style={style} className={`min-w-0 max-w-[78ch] normal-case tracking-normal ${className} [overflow-wrap:anywhere] [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a:focus-visible]:outline-2 [&_a:focus-visible]:outline-primary [&_a:focus-visible]:outline-offset-2 [&_p]:mb-4 [&>:last-child]:mb-0 [&_h1]:mb-3 [&_h1]:mt-6 [&_h1]:font-semibold [&_h2]:mb-3 [&_h2]:mt-6 [&_h2]:font-semibold [&_h3]:mb-2 [&_h3]:mt-5 [&_h3]:font-semibold [&_ul]:mb-4 [&_ul]:list-disc [&_ol]:mb-4 [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:my-1 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_pre]:whitespace-pre-wrap [&_pre]:break-words [&_code]:whitespace-pre-wrap`}>
    <ReactMarkdown skipHtml urlTransform={url => /^doc:\/\//i.test(url) ? url : defaultUrlTransform(url)}
      components={{
        img: ({ alt }) => <span>{alt ?? ''}</span>,
        p: ({ children }) => <p>{renderChildren(children)}</p>,
        li: ({ children }) => <li>{renderChildren(children)}</li>,
        a: ({ href, children }) => /^doc:\/\//i.test(href ?? '')
          ? sourceLink(href!, children) : <a href={href || undefined}>{children}</a>,
      }}>{prose}</ReactMarkdown>
  </div>;
}
